import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { PropTypes } from 'prop-types';
import { useStores } from 'hooks';
import { Icon, Select, Input } from 'components/project/ClerkScreen';
import { isEnterKey, isTabKey } from '../../shared/utils/common';
import {
  Container, Label, FilterButton, InputButtonGroup,
} from './Styles';

const ChatFilter = ({ laneId }) => {
  const {
    BidStore: { auction },
    UIStore: { handleChatFilter },
  } = useStores();

  const auctionLane = auction[laneId];
  const chatFilterType = auctionLane?.chatFilter?.filterType ?? '';
  const chatFilterValue = auctionLane?.chatFilter?.filterValue ?? '';

  const filterValueRef = useRef(null);
  const filterTypeRef = useRef(null);

  const [filterValue, setFilterValue] = useState(chatFilterValue);
  const [filterType, setFilterType] = useState(chatFilterType);
  const [placeholderText, setPlaceholderText] = useState(null);

  const showInputFilterTypes = ['itemNumber', 'content', 'member', 'account'];
  const showInput = showInputFilterTypes.includes(filterType);

  const filterSelectionData = [
    { value: '', text: 'All Messages', placeholderText: null },
    { value: 'member', text: 'Member', placeholderText: 'Enter member' },
    { value: 'account', text: 'Account', placeholderText: 'Enter account' },
    { value: 'itemNumber', text: 'Item #', placeholderText: 'Enter lot number' },
    {
      value: 'memberName', text: 'Global Messages', typeValue: null, placeholderText: null,
    },
    { value: 'content', text: 'Content', placeholderText: 'Enter message text' },
  ];

  const getPlaceholderText = (value) => filterSelectionData.filter((item) => item.value === value)[0].placeholderText;

  const onFilterSelectionChange = (e) => {
    const doShowInput = showInputFilterTypes.includes(e.target.value);
    setFilterType(e.target.value);
    if (!doShowInput) {
      setPlaceholderText(null);
      if (e.target.value === 'type') {
        setFilterValue('MessageToLane');
        handleChatFilter(laneId, e.target.value, 'MessageToLane');
      } else {
        handleChatFilter(laneId, e.target.value, e.target.text);
      }
    } else {
      setPlaceholderText(getPlaceholderText(e.target.value));
      setFilterValue('');
      setFilterType(e.target.value);
    }
  };

  const onFilterInputChange = (e) => setFilterValue(e.target.value);
  const onFilterKeyUp = () => {};
  const onFilterClick = () => {
    handleChatFilter(laneId, filterType, filterValue);
  };

  const onSelectKeyPress = (e) => {
    e.preventDefault();

    if (isEnterKey(e)) {
      onFilterClick();
    }
  };

  const onInputKeyPress = (e) => {
    if (isEnterKey(e)) {
      e.preventDefault();
      onFilterClick();
      return;
    }

    if (isTabKey(e)) {
      return;
    }

    setFilterValue(e.target.value);
  };

  const onFilterClearClick = () => {
    setFilterValue('');
    setFilterType('');
    setPlaceholderText(null);
    handleChatFilter(laneId, '', '');
  };

  useEffect(() => {
    if (showInput) filterValueRef.current.focus();
    return () => null;
  }, [filterType, showInput]);

  return (
    <Container>
      <Label>Type</Label>
      <Select
        ref={filterTypeRef}
        valueProp="value"
        textProp="text"
        value={filterType}
        currentValue={filterType}
        data={filterSelectionData}
        noneSelectedText=""
        onChange={onFilterSelectionChange}
        onKeyPress={onSelectKeyPress}
      />
      {showInput && <Label>Value</Label>}
      <InputButtonGroup showInput={showInput}>
        {showInput && (
          <Input
            key={placeholderText}
            autoComplete="off"
            tabIndex={-1}
            placeholderText={placeholderText}
            disabled={false}
            ref={filterValueRef}
            value={filterValue}
            onKeyUp={onFilterKeyUp}
            onKeyPress={onInputKeyPress}
            onChange={onFilterInputChange}
            maxLength={50}
            testId="filter-text"
          />
        )}
        {showInput && (
        <FilterButton className="primary" onClick={onFilterClick}>
          <Icon icon="filter" size={1.1} />
        </FilterButton>
        )}
        <FilterButton className="primary" onClick={onFilterClearClick}>
          <Icon icon="times" size={1.1} />
        </FilterButton>
      </InputButtonGroup>
    </Container>
  );
};

ChatFilter.propTypes = {
  laneId: PropTypes.string,
};

ChatFilter.defaultProps = {
  laneId: null,
};

export default observer(ChatFilter);
